/* eslint-disable max-lines */
import { EFeatureFlags, FeatureFlagConfigs } from './featureFlags';

export enum EMetricsRegistry {
  NetOee = 'netOee',
  Annotation = 'annotation',
  NetAvailability = 'netAvailability',
  TargetAvailability = 'targetAvailability',
  CurrentAvailability = 'currentAvailability',
  PreviousAvailability = 'previousAvailability',
  AverageAvailabilityByWeekDay = 'averageAvailabilityByWeekDay',
  AverageAvailabilityByWeekNight = 'averageAvailabilityByWeekNight',
  AverageAvailabilityByWeekMorning = 'averageAvailabilityByWeekMorning',
  AverageAvailabilityByWeekAfternoon = 'averageAvailabilityByWeekAfternoon',
  OeeOee = 'oee_oee',
  OeeQuality = 'oee_quality',
  OeeProduced = 'oee_produced',
  OeeEffectiveness = 'oee_effectiveness'
}

export enum EMachineConfigUIViewType {
  TvKpi = 'tvKpi',
  DesktopNonKpi = 'desktopNonKpi',
  TvSingleMachine = 'tvSingleMachine',
  TvTowerView2x2 = 'tvTowerView2x2',
  TvTowerView3x3 = 'tvTowerView3x3',
  TvTowerView6x3 = 'tvTowerView6x3'
}

export enum ETvModeLayout {
  Single = 'single',
  Grid2x2 = 'grid2x2',
  Grid3x3 = 'grid3x3',
  Grid6x3 = 'grid6x3'
}

export type MachineConfigUICountSensorEnum =
  | 'accumulatedCount'
  | 'currentMinuteCount'
  | 'sensors_oee'
  | 'sensors_instantaneous_velocity'
  | 'sensors_avg_velocity'
  | 'sensors_aggregated_production'
  | 'hourByHourDefects'
  | 'hourByHourProducts';
export type MachineConfigUIProductionBasicAndProdFullEnum =
  | 'currentMinuteCount'
  | 'instantaneous_velocity'
  | 'avg_velocity'
  | 'total'
  | 'defects'
  | 'scrap'
  | 'correct';
export type MachineConfigUIAvailabilityEnum =
  | 'availability'
  | 'annotation'
  | 'netAvailability'
  | 'targetAvailability'
  | 'previousAvailability'
  | 'currentAvailability'
  | 'averageAvailabilityByWeekAfternoon'
  | 'averageAvailabilityByWeekDay'
  | 'averageAvailabilityByWeekMorning'
  | 'averageAvailabilityByWeekNight';
export type MachineConfigUIProductionsEnum =
  | 'oee_quality'
  | 'oee_produced'
  | 'oee_effectiveness'
  | 'oee_oee'
  | 'netOee';
export type MachineConfigUIMetricEnum =
  | MachineConfigUICountSensorEnum
  | MachineConfigUIProductionBasicAndProdFullEnum
  | MachineConfigUIAvailabilityEnum
  | MachineConfigUIProductionsEnum;

export enum EUnorderedMetricEnum {
  HourByHourDefects = 'hourByHourDefects',
  HourByHourProducts = 'hourByHourProducts'
}

export enum EFeatureFlagComparison {
  Or = 'or',
  And = 'and'
}

export type MachineConfigUIParameter = {
  name: string;
  type: 'number' | 'option';
  unit?: string;
  options?: string[];
  min?: number;
  max?: number;
  value?: string | number;
};

export type MachineConfigUIMetricContent = {
  metric: MachineConfigUIMetricEnum;
  enabled: boolean;
  order: number;
  parameters?: MachineConfigUIParameter[];
  featureFlagRequired?: EFeatureFlags[];
};

export type MachineConfigUICatalogContent = {
  machines?: number[];
  viewType: EMachineConfigUIViewType[];
  parameters?: MachineConfigUIParameter[];
  comparison: EFeatureFlagComparison;
  featureFlagRequired?: EFeatureFlags[];
};

export type MachineConfigUICatalog = {
  [metricEnum in MachineConfigUIMetricEnum]: MachineConfigUICatalogContent;
};

type ConfigValue =
  | {
      [key: string]: ConfigValue;
    }
  | (string | boolean | null | number);

type SPCConfig = {
  OEE: boolean;
  legacyOEE: boolean;
  legacyMetrics: boolean;
};

type IntegrationConfig = {
  OEE: boolean;
  legacyOEE: boolean;
  localtime: boolean;
  overrides: { speeds: boolean; targets: boolean; workLogs: boolean };
  dateFormat: string;
};

type ProductionConfig = {
  enabled: boolean;
  scrapEnabled: boolean;
  aceromexExclusiveFeatures: boolean;
  estimatedDeliveryTimeEnabled: boolean;
};

type GridConfig = {
  rows: number;
  columns: number;
};

type UnknownMetrics = Record<string, ConfigValue>;
type KnownMetrics = Record<EMetricsRegistry, ConfigValue>;

export type Metrics = Partial<KnownMetrics & UnknownMetrics>;

export type RealtimeConfig = {
  metrics: Metrics;
  realtimeNonKpi: Metrics;
  showOeeMetrics: boolean;
};

export type UserConfig = {
  spc: SPCConfig;
  logo: string;
  work: { unitA: number };
  showDSO: boolean;
  isEquity: boolean;
  realtime?: RealtimeConfig;
  integrations: IntegrationConfig;
  production: ProductionConfig;
  showVotings: boolean;
  smsQuota: number;
  background: string;
  /**
   * @deprecated but still supported
   */
  kpisTvMode: GridConfig;
  /**
   * @deprecated
   */
  legacyTvMode: GridConfig;
  shiftSettings: {
    allowEditionUpToDays: number;
    moveNightToNextDay: boolean;
  };
  showDividends: boolean;
  smsQuotaStart: Date;
  backgroundColor: string;
  showInnerTrading: boolean;
  lavados: { enabled: boolean };
  featureFlags: FeatureFlagConfigs;
  machineMetricCatalog: MachineConfigUICatalog;
  machineMetricConfig: {
    [machineId: string]: {
      [viewType in EMachineConfigUIViewType]: MachineConfigUIMetricContent[];
    };
  };
  tvGridMode: {
    mode: ETvModeLayout;
  };
};

// this is mostly just an alias
export type UIMetric = Omit<
  MachineConfigUIMetricContent,
  'featureFlagRequired'
>;
